import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ChatSidebar from '../components/chat/ChatSidebar';
import ChatInterface from '../components/chat/ChatInterface';
import '../styles/chat-page.css';
import '../styles/chat-sidebar.css';
import { useIsMobile } from '../hooks/useIsMobile';
import { useViewportHeight } from '../hooks/useViewportHeight';

// Generate UUID v4 for session IDs
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

const ChatPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  // Get initial prompt from URL query parameter
  const initialPrompt = searchParams.get('prompt');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const isMobile = useIsMobile();
  const [isTablet, setIsTablet] = useState(false);

  useViewportHeight();

  // Lock document scrolling while the chat is open — the chat column owns
  // all scrolling, and a scrollable document is what makes iOS rubber-band.
  useEffect(() => {
    document.documentElement.classList.add('chat-fullscreen');
    return () => document.documentElement.classList.remove('chat-fullscreen');
  }, []);

  // Responsive breakpoints
  useEffect(() => {
    const checkBreakpoints = () => {
      const width = window.innerWidth;
      setIsTablet(width >= 768 && width <= 991);
    };
    checkBreakpoints();
    window.addEventListener('resize', checkBreakpoints);
    return () => window.removeEventListener('resize', checkBreakpoints);
  }, []);

  // Determine if sidebar should be drawer or persistent
  const useMobileDrawer = isMobile || isTablet;

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Load conversations function (extracted for reuse)
  const loadConversations = async () => {
    try {
      const token = localStorage.getItem('userToken');
      if (!token) {
        setConversations([]);
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/api/agent/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.success && data.data && data.data.length > 0) {
        console.log(`[ChatPage] Loaded ${data.data.length} conversations`);
        // Merge backend data with local-only conversations
        setConversations(prev => {
          const backendIds = new Set(data.data.map(c => c.sessionId || c.id));
          const localOnly = prev.filter(c => !backendIds.has(c.sessionId || c.id));

          // Backend conversations first (newer data), then local-only
          return [...data.data, ...localOnly];
        });
      } else {
        console.log('[ChatPage] No conversations found');
        setConversations([]);
      }
    } catch (error) {
      console.error('[ChatPage] Error loading conversations:', error);
      setConversations([]);
    }
  };

  // Load conversations on mount
  useEffect(() => {
    if (user) {
      loadConversations();
    }
  }, [user]);

  // Auto-select most recent conversation if none is selected
  useEffect(() => {
    if (!currentConversationId && conversations.length > 0) {
      console.log('[ChatPage] Auto-selecting most recent conversation');
      setCurrentConversationId(conversations[0].sessionId || conversations[0].id);
    }
  }, [conversations, currentConversationId]);

  // Periodically refresh conversations to pick up backend updates
  useEffect(() => {
    if (!user) return;

    // Refresh every 10 seconds while the page is active
    const intervalId = setInterval(() => {
      loadConversations();
    }, 10000);

    return () => clearInterval(intervalId);
  }, [user]);

  // Handle new conversation
  const handleNewConversation = () => {
    // STEP 1: Ensure current conversation is in the list before switching
    if (currentConversationId) {
      const existingConv = conversations.find(
        conv => (conv.sessionId || conv.id) === currentConversationId
      );

      // Add current conversation if not already in list (defensive)
      if (!existingConv) {
        console.log('[ChatPage] Adding current conversation to history before creating new one');
        setConversations(prev => [{
          sessionId: currentConversationId,
          totalMessages: 0, // Will be updated by backend polling
          lastActivity: new Date().toISOString(),
          stocksDiscussed: []
        }, ...prev]);
      }
    }

    // STEP 2: Generate new session ID and add to list
    const newSessionId = generateUUID();
    console.log(`[ChatPage] Creating new conversation: ${newSessionId}`);

    setConversations(prev => [{
      sessionId: newSessionId,
      totalMessages: 0,
      lastActivity: new Date().toISOString(),
      stocksDiscussed: []
    }, ...prev]);

    // STEP 3: Set as active conversation
    setCurrentConversationId(newSessionId);

    // STEP 4: Close mobile drawer if applicable
    if (useMobileDrawer) {
      setIsSidebarOpen(false);
    }
  };

  // Handle conversation selection
  const handleSelectConversation = (conversationId) => {
    setCurrentConversationId(conversationId);
    if (useMobileDrawer) {
      setIsSidebarOpen(false);
    }
  };

  // Handle delete conversation
  const handleDeleteConversation = async (conversationId) => {
    try {
      const token = localStorage.getItem('userToken');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

      const response = await fetch(`${apiUrl}/api/agent/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.success) {
        // Remove from local state
        setConversations(prev => prev.filter(conv => conv.sessionId !== conversationId));

        // If deleting current conversation, start a new one
        if (conversationId === currentConversationId) {
          setCurrentConversationId(null);
        }
      }
    } catch (error) {
      console.error('[ChatPage] Error deleting conversation:', error);
    }
  };

  // Toggle sidebar/drawer
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Close drawer when clicking backdrop (mobile only)
  const handleBackdropClick = () => {
    if (useMobileDrawer) {
      setIsSidebarOpen(false);
    }
  };

  // Handle swipe gesture from left edge (mobile)
  useEffect(() => {
    if (!useMobileDrawer) return;

    let touchStartX = 0;
    let touchEndX = 0;

    const handleTouchStart = (e) => {
      touchStartX = e.touches[0].clientX;
    };

    const handleTouchEnd = (e) => {
      touchEndX = e.changedTouches[0].clientX;

      // Swipe from left edge (< 50px from left)
      if (touchStartX < 50 && touchEndX > touchStartX + 100) {
        setIsSidebarOpen(true);
      }
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [useMobileDrawer]);

  if (!user) {
    return null; // Will redirect to login
  }

  return (
    <div className="chat-page">
      {/* Backdrop overlay for mobile drawer */}
      {useMobileDrawer && isSidebarOpen && (
        <div
          className="chat-backdrop"
          onClick={handleBackdropClick}
          aria-hidden="true"
        />
      )}

      {/* Sidebar/Drawer */}
      <ChatSidebar
        isOpen={isSidebarOpen}
        isMobile={useMobileDrawer}
        conversations={conversations}
        currentConversationId={currentConversationId}
        onNewConversation={handleNewConversation}
        onSelectConversation={handleSelectConversation}
        onDeleteConversation={handleDeleteConversation}
        onClose={() => setIsSidebarOpen(false)}
      />

      {/* Main Chat Interface */}
      <div className={`chat-main ${useMobileDrawer ? 'mobile' : 'desktop'}`}>
        <ChatInterface
          conversationId={currentConversationId}
          conversations={conversations}
          onToggleSidebar={toggleSidebar}
          onNewConversation={handleNewConversation}
          onSelectConversation={handleSelectConversation}
          onDeleteConversation={handleDeleteConversation}
          onConversationsUpdate={setConversations}
          showMenuButton={useMobileDrawer}
          initialPrompt={initialPrompt}
          onInitialPromptUsed={() => {
            // Clear the prompt from URL after it's been sent
            setSearchParams({});
          }}
        />
      </div>
    </div>
  );
};

export default ChatPage;
